// src/pages/students/details/EnrollmentsTab.jsx
import { useState, useMemo, useEffect, useRef, useCallback } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { supabase } from '../../../supabaseClient';
import { useStudents } from '../../../hooks/useStudents';
import { useIntakes } from '../../../hooks/useIntakes';
import { useSubjects } from '../../../hooks/useSubjects';
import Card from '../../../components/common/Card';
import {
  AcademicCapIcon,
  CalendarIcon,
  CheckCircleIcon,
  ClockIcon,
  XCircleIcon,
  PlusIcon,
  PencilIcon,
  TrashIcon,
} from '@heroicons/react/24/outline';
import BulkEnrollmentStatusModal from './BulkEnrollmentStatusModal'; // ⬅ NEW

export default function EnrollmentsTab({ studentId }) {
  const location = useLocation();
  const queryClient = useQueryClient();
  const { getStudentEnrollments, deleteEnrollment } = useStudents();
  const { intakes } = useIntakes();
  const { subjects } = useSubjects();

  // UI state
  const [expandedIntakes, setExpandedIntakes] = useState({});
  const [deletingId, setDeletingId] = useState(null);
  const [isBulkModalOpen, setIsBulkModalOpen] = useState(false); // ⬅ NEW

  // keep latest ref for queryFn
  const getStudentEnrollmentsRef = useRef(getStudentEnrollments);
  useEffect(() => {
    getStudentEnrollmentsRef.current = getStudentEnrollments;
  }, [getStudentEnrollments]);

  // ----- Helpers -----
  const formatDate = (dateString) => {
    if (!dateString) return 'Invalid Date';
    try {
      const date = new Date(dateString);
      if (isNaN(date.getTime())) return 'Invalid Date';
      return date.toLocaleDateString();
    } catch {
      return 'Invalid Date';
    }
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'Active':
        return <ClockIcon className="h-4 w-4 text-yellow-500" />;
      case 'Completed':
        return <CheckCircleIcon className="h-4 w-4 text-green-500" />;
      case 'Withdrawn':
        return <XCircleIcon className="h-4 w-4 text-red-500" />;
      default:
        return <ClockIcon className="h-4 w-4 text-gray-500" />;
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'Active':
        return 'bg-yellow-100 text-yellow-800';
      case 'Completed':
        return 'bg-green-100 text-green-800';
      case 'Withdrawn':
        return 'bg-red-100 text-red-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  const getSubjectById = (id) => subjects.find((s) => s.id === id);

  const groupEnrollmentsByIntake = useCallback(
    (enrs) => {
      const grouped = {};
      if (!enrs?.length) return grouped;

      enrs.forEach((enrollment) => {
        let intake = enrollment.intake;
        if (!intake && enrollment.intake_id) {
          intake = intakes.find((i) => i.id === enrollment.intake_id);
        }
        if (!intake) return;

        const intakeId = intake.id;
        if (!grouped[intakeId]) grouped[intakeId] = { ...intake, enrollments: [] };

        let subject = enrollment.subject;
        if (!subject && enrollment.subject_id) {
          subject = getSubjectById(enrollment.subject_id);
        }
        const enhanced = {
          ...enrollment,
          subject: subject || { id: enrollment.subject_id, code: 'Unknown', name: 'Subject Not Found' },
        };
        grouped[intakeId].enrollments.push(enhanced);
      });

      // Sort subjects in each intake
      Object.keys(grouped).forEach((intakeId) => {
        const list = grouped[intakeId].enrollments || [];
        list.sort((a, b) => {
          if (a.subject?.code && b.subject?.code) {
            const cmp = a.subject.code.localeCompare(b.subject.code);
            if (cmp !== 0) return cmp;
          }
          if (a.subject?.name && b.subject?.name) {
            return a.subject.name.localeCompare(b.subject.name);
          }
          return new Date(a.enrollment_date) - new Date(b.enrollment_date);
        });
      });

      return grouped;
    },
    [intakes, subjects]
  );

  const calculateIntakeProgress = (intakeEnrollments) => {
    const total = intakeEnrollments.length;
    if (!total) return 0;
    const completed = intakeEnrollments.filter((e) => e.status === 'Completed').length;
    return Math.round((completed / total) * 100);
  };

  const toggleIntakeExpansion = (intakeId) =>
    setExpandedIntakes((prev) => ({ ...prev, [intakeId]: !prev[intakeId] }));

  // ----- Data: enrollments -----
  const {
    data: enrollments = [],
    isLoading: enrollmentsInitialLoading,
    isFetching: enrollmentsFetching,
  } = useQuery({
    queryKey: ['enrollments', studentId],
    queryFn: async () => {
      const res = await getStudentEnrollmentsRef.current(studentId);
      if (res?.success) return res.data || [];
      console.error('Failed to fetch enrollments:', res?.error);
      return [];
    },
    enabled: !!studentId,
    // ⬇️ ensure we get the latest when coming back
    refetchOnMount: 'always',
    refetchOnWindowFocus: true,
    staleTime: 0,
    placeholderData: (prev) => prev,
  });

  // ----- Data: paid enrollment ids -----
  const { data: paidEnrollmentIds = [] } = useQuery({
    queryKey: ['paid_enrollment_ids', studentId],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('payment_enrollments')
        .select('enrollment_id, payments!inner(status, student_id, is_deleted)')
        .eq('payments.student_id', studentId)
        .eq('payments.is_deleted', false)
        .in('payments.status', ['Paid', 'paid', 'PAID']);
      if (error) {
        console.error('Error fetching paid enrollments:', error);
        return [];
      }
      return (data || []).map((row) => String(row.enrollment_id));
    },
    enabled: !!studentId,
    refetchOnMount: 'always',
    refetchOnWindowFocus: true,
    staleTime: 0,
    placeholderData: (prev) => prev,
  });

  // ⬇️ When navigating back to this route (location.key changes on POP),
  //    force a fresh read for both queries.
  useEffect(() => {
    if (!studentId) return;
    queryClient.invalidateQueries({ queryKey: ['enrollments', studentId] });
    queryClient.invalidateQueries({ queryKey: ['paid_enrollment_ids', studentId] });
    // Optionally kick immediate refetch (not required if refetchOnMount handles it)
    queryClient.refetchQueries({ queryKey: ['enrollments', studentId] });
    queryClient.refetchQueries({ queryKey: ['paid_enrollment_ids', studentId] });
  }, [location.key, studentId, queryClient]);

  const paidSet = useMemo(() => new Set(paidEnrollmentIds), [paidEnrollmentIds]);
  const enrollmentsRefreshing = enrollmentsFetching && !!enrollments?.length;

  // Grouping + latest intake id
  const groupedEnrollments = useMemo(
    () => groupEnrollmentsByIntake(enrollments),
    [groupEnrollmentsByIntake, enrollments]
  );

  const sortedIntakeIds = useMemo(() => {
    const ids = Object.keys(groupedEnrollments);
    return ids.sort((a, b) => {
      const dateA = groupedEnrollments[a].start_date ? new Date(groupedEnrollments[a].start_date) : new Date(0);
      const dateB = groupedEnrollments[b].start_date ? new Date(groupedEnrollments[b].start_date) : new Date(0);
      return dateB - dateA;
    });
  }, [groupedEnrollments]);

  const latestIntakeId = sortedIntakeIds.length > 0 ? sortedIntakeIds[0] : '';

  // Actions
  const handleRemoveEnrollment = async (enrollmentId) => {
    const ok = window.confirm('Remove this enrollment? This is a soft delete and can be re-added later.');
    if (!ok) return;

    setDeletingId(enrollmentId);

    // Optimistic: remove from cache immediately
    queryClient.setQueryData(['enrollments', studentId], (prev = []) => prev.filter((e) => e.id !== enrollmentId));

    try {
      const res = await deleteEnrollment(enrollmentId);
      if (!res?.success) {
        await queryClient.invalidateQueries({ queryKey: ['enrollments', studentId] });
        alert(res?.error || 'Failed to remove enrollment.');
      } else {
        queryClient.invalidateQueries({ queryKey: ['enrollments', studentId] });
      }
    } catch (err) {
      console.error(err);
      await queryClient.invalidateQueries({ queryKey: ['enrollments', studentId] });
      alert('Unexpected error while removing enrollment.');
    } finally {
      setDeletingId(null);
    }
  };

  // ----- UI -----
  return (
    <>
      <Card>
        <div className="space-y-4">
          <div className="flex justify-between items-center">
            {/* Left: title + count */}
            <div className="flex items-center gap-3">
              <h2 className="text-lg font-medium text-gray-900">Enrollments</h2>
              <div className="text-sm text-gray-500">
                {enrollments.length} total enrollments
                {enrollmentsRefreshing && <span className="ml-2 text-xs text-gray-400">Refreshing…</span>}
              </div>
            </div>

            {/* Right: Bulk Update + Add Enrollment */}
            <div className="flex items-center gap-2">
              <button
                type="button"
                onClick={() => setIsBulkModalOpen(true)}
                className="inline-flex items-center border border-gray-300 bg-white hover:bg-gray-50 text-gray-700 px-3 py-1.5 rounded-md text-sm"
              >
                Bulk Update Status
              </button>

              <Link
                to={`/students/${studentId}/enrollments/new${
                  Object.keys(groupedEnrollments).length
                    ? `?intake=${Object.keys(groupedEnrollments).sort((a, b) => {
                        const dateA = groupedEnrollments[a].start_date
                          ? new Date(groupedEnrollments[a].start_date)
                          : new Date(0);
                        const dateB = groupedEnrollments[b].start_date
                          ? new Date(groupedEnrollments[b].start_date)
                          : new Date(0);
                        return dateB - dateA;
                      })[0]}`
                    : ''
                }`}
                className="inline-flex items-center bg-gray-700 hover:bg-gray-800 text-white px-3 py-1.5 rounded-md text-sm"
              >
                <PlusIcon className="h-4 w-4 mr-1" />
                Add Enrollment
              </Link>
            </div>
          </div>

          {enrollmentsInitialLoading && enrollments.length === 0 ? (
            <div className="py-8 text-center">
              <p className="text-gray-500">Loading enrollments…</p>
            </div>
          ) : enrollments.length === 0 ? (
            <div className="py-12 text-center text-gray-500">
              No enrollments yet{' '}
              <Link
                to={`/students/${studentId}/enrollments/new${latestIntakeId ? `?intake=${latestIntakeId}` : ''}`}
                className="text-blue-600 hover:text-blue-800 underline"
              >
                Add Enrollment
              </Link>
            </div>
          ) : Object.keys(groupedEnrollments).length === 0 ? (
            <div className="py-8 text-center">
              <p className="text-gray-500">No intake information available for enrollments.</p>
            </div>
          ) : (
            <div className="space-y-6">
              {Object.keys(groupedEnrollments)
                .sort((a, b) => {
                  const dateA = groupedEnrollments[a].start_date ? new Date(groupedEnrollments[a].start_date) : new Date(0);
                  const dateB = groupedEnrollments[b].start_date ? new Date(groupedEnrollments[b].start_date) : new Date(0);
                  return dateB - dateA;
                })
                .map((intakeId) => {
                  const intake = groupedEnrollments[intakeId];
                  const progress = calculateIntakeProgress(intake.enrollments);
                  const isExpanded = !!expandedIntakes[intakeId];

                  return (
                    <div key={intakeId} className="border border-gray-200 rounded-lg overflow-hidden">
                      {/* Intake Header */}
                      <div
                        className="bg-gray-50 px-4 py-3 flex justify-between items-center cursor-pointer"
                        onClick={() => toggleIntakeExpansion(intakeId)}
                      >
                        <div className="flex items-center space-x-3">
                          <AcademicCapIcon className="h-5 w-5 text-gray-600" />
                          <div>
                            <h3 className="font-medium text-gray-900">{intake.name}</h3>
                            {intake.start_date && intake.end_date ? (
                              <div className="flex items-center text-sm text-gray-500">
                                <CalendarIcon className="h-4 w-4 mr-1" />
                                <span>
                                  {formatDate(intake.start_date)} - {formatDate(intake.end_date)}
                                </span>
                              </div>
                            ) : (
                              <div className="text-sm text-gray-500">Dates not available</div>
                            )}
                          </div>
                        </div>

                        <div className="flex items-center space-x-4">
                          <div className="text-sm">
                            <span className="font-medium text-gray-700">{progress}%</span> completed
                          </div>
                          <div className="w-24 bg-gray-200 rounded-full h-2">
                            <div className="bg-blue-600 h-2 rounded-full" style={{ width: `${progress}%` }} />
                          </div>
                          <div className="text-sm text-gray-500">{intake.enrollments.length} subjects</div>
                          <svg
                            className={`h-5 w-5 text-gray-500 transform transition-transform ${isExpanded ? 'rotate-180' : ''}`}
                            fill="none"
                            viewBox="0 0 24 24"
                            stroke="currentColor"
                          >
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                          </svg>
                        </div>
                      </div>

                      {/* Subject List */}
                      {isExpanded && (
                        <div className="divide-y divide-gray-200">
                          {intake.enrollments.map((enrollment) => (
                            <div key={enrollment.id} className="px-4 py-3 flex justify-between items-center">
                              <div className="flex items-center space-x-3">
                                <div className="flex-shrink-0">{getStatusIcon(enrollment.status)}</div>
                                <div>
                                  <div className="font-medium text-gray-900">
                                    {enrollment.subject?.code} - {enrollment.subject?.name}
                                  </div>
                                  <div className="text-sm text-gray-500">
                                    Enrolled: {formatDate(enrollment.enrollment_date)}
                                  </div>
                                </div>
                              </div>

                              <div className="flex items-center space-x-3">
                                <span
                                  className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full ${getStatusColor(
                                    enrollment.status
                                  )}`}
                                >
                                  {enrollment.status}
                                </span>

                                {/* Paid / Not Paid pill */}
                                <span
                                  className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full ${
                                    paidSet.has(String(enrollment.id))
                                      ? 'bg-green-100 text-green-800'
                                      : 'bg-gray-100 text-gray-800'
                                  }`}
                                >
                                  {paidSet.has(String(enrollment.id)) ? 'Paid' : 'Not Paid'}
                                </span>

                                <Link
                                  to={`/enrollments/${enrollment.id}/edit?fromStudent=${studentId}&intake=${enrollment.intake_id}`}
                                  className="inline-flex items-center text-indigo-600 hover:text-indigo-800 text-sm"
                                  title="Edit enrollment"
                                >
                                  <PencilIcon className="h-4 w-4 mr-1" />
                                  Edit
                                </Link>
                                <button
                                  onClick={() => handleRemoveEnrollment(enrollment.id)}
                                  disabled={deletingId === enrollment.id}
                                  className="inline-flex items-center text-red-600 hover:text-red-800 text-sm disabled:opacity-50"
                                  title="Remove enrollment"
                                >
                                  <TrashIcon className="h-4 w-4 mr-1" />
                                  {deletingId === enrollment.id ? 'Removing…' : 'Remove'}
                                </button>
                              </div>
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  );
                })}
            </div>
          )}
        </div>
      </Card>

      {/* Bulk Update Modal */}
      <BulkEnrollmentStatusModal
        studentId={studentId}
        paidEnrollmentIds={paidEnrollmentIds}
        isOpen={isBulkModalOpen}
        onClose={() => setIsBulkModalOpen(false)}
      />

    </>
  );
}
